import { chapters, type GameAction, type GameSnapshot } from "@gunnrats23/shared";
import { OnlineAdapter, SoloAdapter, type GameAdapter } from "../net/adapters";
import type { LabScene } from "../game/LabScene";

export class AppController {
  private root: HTMLElement;
  private scene: LabScene;
  private adapter: GameAdapter | undefined;
  private snapshot: GameSnapshot | undefined;
  private hintOpen = false;
  private journalOpen = false;
  private error = "";

  constructor(root: HTMLElement, scene: LabScene) {
    this.root = root;
    this.scene = scene;
    this.renderMenu();
  }

  private renderMenu() {
    this.root.innerHTML = `
      <section class="start-shell">
        <div class="start-window">
          <div class="window-title">
            <span>Gunnrats23.exe</span>
            <span class="window-buttons"><i></i><i></i><i></i></span>
          </div>
          <div class="start-body">
            <div class="logo-block">
              <span class="pixel-mark">G23</span>
              <h1>Gunnrats23</h1>
              <p>A retro science game about FGF23, Bilirubin, messy data, and better questions.</p>
            </div>
            <div class="menu-grid">
              <button data-action="solo" class="primary">Solo Campaign</button>
              <button data-action="host">Host Online Lab</button>
              <label>
                Player name
                <input id="player-name" maxlength="24" value="Sascha" />
              </label>
              <label>
                Join code
                <input id="room-code" maxlength="12" placeholder="ROOM ID" />
              </label>
              <button data-action="join">Join Lab Room</button>
              <button data-action="challenge">Daily Challenge</button>
            </div>
            <div class="system-note">
              ${this.error ? `<strong>${escapeHtml(this.error)}</strong>` : "Online rooms sync through the stage server. Solo saves locally in this browser."}
            </div>
          </div>
        </div>
      </section>
    `;
    this.bindMenu();
  }

  private bindMenu() {
    this.root.querySelector<HTMLButtonElement>('[data-action="solo"]')?.addEventListener("click", () => {
      this.connect(new SoloAdapter());
    });
    this.root.querySelector<HTMLButtonElement>('[data-action="challenge"]')?.addEventListener("click", () => {
      const adapter = new SoloAdapter();
      this.connect(adapter);
      adapter.dispatch({ type: "setChapter", chapterIndex: 6 });
    });
    this.root.querySelector<HTMLButtonElement>('[data-action="host"]')?.addEventListener("click", async () => {
      await this.online("host");
    });
    this.root.querySelector<HTMLButtonElement>('[data-action="join"]')?.addEventListener("click", async () => {
      await this.online("join");
    });
  }

  private async online(mode: "host" | "join") {
    const name = this.root.querySelector<HTMLInputElement>("#player-name")?.value || "Researcher";
    const code = this.root.querySelector<HTMLInputElement>("#room-code")?.value || "";
    this.error = "";
    this.renderMenu();
    try {
      const adapter =
        mode === "host" ? await OnlineAdapter.host(name) : await OnlineAdapter.join(name, code);
      this.connect(adapter);
    } catch (error) {
      this.error = error instanceof Error ? error.message : "Could not connect to lab room.";
      this.renderMenu();
    }
  }

  private connect(adapter: GameAdapter) {
    this.adapter?.disconnect();
    this.adapter = adapter;
    adapter.onSnapshot((snapshot) => {
      this.snapshot = snapshot;
      this.scene.updateSnapshot(snapshot);
      this.renderGame();
    });
  }

  private renderGame() {
    if (!this.snapshot) {
      return;
    }
    const s = this.snapshot;
    const selected = new Set(s.selectedIds);
    const room = s.roomId ? `<button class="code-pill" data-copy-code>${escapeHtml(s.roomId)}</button>` : `<span class="code-pill">LOCAL</span>`;
    const choices = s.task.choices
      .map(
        (choice) => `
        <button class="choice-card ${selected.has(choice.id) ? "selected" : ""}" data-choice="${choice.id}" style="--card-accent:${choice.color}">
          <span class="choice-icon">${escapeHtml(choice.icon)}</span>
          <span class="choice-copy">
            <strong>${escapeHtml(choice.label)}</strong>
            <em>${escapeHtml(choice.domain)}</em>
            <small>${escapeHtml(choice.blurb)}</small>
          </span>
        </button>`
      )
      .join("");

    this.root.innerHTML = `
      <section class="game-shell">
        <header class="top-bar">
          <div>
            <strong>Gunnrats23</strong>
            <span>${escapeHtml(s.modeLabel)}</span>
          </div>
          <div class="hud-cluster">
            ${room}
            <span>Score ${s.score}</span>
            <span>Mistakes ${s.mistakes}</span>
            <button data-action="journal">Journal ${s.journal.length}</button>
            <button data-action="menu">Menu</button>
          </div>
        </header>
        <aside class="chapter-rail">
          ${chapters
            .map(
              (chapter, index) => `
              <button class="${index === s.chapterIndex ? "active" : ""} ${s.completedChapterIds.includes(chapter.id) ? "complete" : ""}" data-chapter="${index}">
                <span>${index + 1}</span>
                <strong>${escapeHtml(chapter.title)}</strong>
              </button>`
            )
            .join("")}
        </aside>
        <main class="play-panel">
          <div class="window-title">
            <span>${escapeHtml(s.progressLabel)}</span>
            <span class="window-buttons"><i></i><i></i><i></i></span>
          </div>
          <section class="mission">
            <p class="objective">${escapeHtml(s.chapter.objective)}</p>
            <h2>${escapeHtml(s.chapter.title)}</h2>
            <p>${escapeHtml(s.chapter.narrative)}</p>
          </section>
          ${this.renderTaskPanel(s, choices)}
          <section class="status-line">${escapeHtml(s.log)}</section>
        </main>
        <aside class="player-panel">
          <h3>Lab Room</h3>
          ${s.players
            .map(
              (player) => `
                <div class="player-row" style="--player:${player.color}">
                  <span></span>
                  <strong>${escapeHtml(player.name)}</strong>
                  <em>${escapeHtml(player.role)}</em>
                </div>`
            )
            .join("")}
          <h3>Concept</h3>
          <p>${escapeHtml(s.task.concept)}</p>
        </aside>
        ${this.journalOpen ? this.renderJournal(s) : ""}
      </section>
    `;
    this.bindGame();
  }

  private renderTaskPanel(s: GameSnapshot, choices: string) {
    if (s.phase === "chapter-complete") {
      return `
        <section class="task-panel complete-panel">
          <h2>${escapeHtml(s.chapter.badge)} unlocked</h2>
          <p>${escapeHtml(s.chapter.journalUnlock)}</p>
          <button data-action="nextChapter" class="primary">Continue to next chapter</button>
        </section>
      `;
    }
    if (s.phase === "finished") {
      return `
        <section class="task-panel complete-panel">
          <h2>Campaign complete</h2>
          <p>The Research Journal is ready. Replay chapters, host a co-op lab, or use this as the warm-up for the real R analysis.</p>
          <button data-action="journal" class="primary">Open Research Journal</button>
          <button data-action="reset">New run</button>
        </section>
      `;
    }
    return `
      <section class="task-panel">
        <div class="task-copy">
          <span class="subtle">${escapeHtml(s.task.brief)}</span>
          <h2>${escapeHtml(s.task.prompt)}</h2>
        </div>
        <div class="choice-grid">${choices}</div>
        <div class="action-row">
          <button data-action="submit" class="primary">Submit cards</button>
          <button data-action="hint">${this.hintOpen ? "Hide hint" : "Hint"}</button>
          <button data-action="reset">Reset run</button>
        </div>
        ${this.hintOpen ? `<p class="hint-box">${escapeHtml(s.task.hint)}</p>` : ""}
      </section>
    `;
  }

  private renderJournal(s: GameSnapshot) {
    const entries = s.journal.length
      ? s.journal.map((entry) => `<li>${escapeHtml(entry)}</li>`).join("")
      : "<li>No journal entries yet. The old PC is waiting for evidence.</li>";
    return `
      <section class="journal-drawer">
        <div class="window-title">
          <span>Research Journal</span>
          <button data-action="journal">Close</button>
        </div>
        <ol>${entries}</ol>
      </section>
    `;
  }

  private bindGame() {
    this.root.querySelectorAll<HTMLButtonElement>("[data-choice]").forEach((button) => {
      button.addEventListener("click", () => {
        this.dispatch({ type: "toggle", choiceId: button.dataset.choice ?? "" });
      });
    });
    this.root.querySelector<HTMLButtonElement>('[data-action="submit"]')?.addEventListener("click", () => {
      this.hintOpen = false;
      this.dispatch({ type: "submit" });
    });
    this.root.querySelectorAll<HTMLButtonElement>('[data-action="hint"]').forEach((button) => {
      button.addEventListener("click", () => {
        this.hintOpen = !this.hintOpen;
        this.renderGame();
      });
    });
    this.root.querySelectorAll<HTMLButtonElement>('[data-action="journal"]').forEach((button) => {
      button.addEventListener("click", () => {
        this.journalOpen = !this.journalOpen;
        this.renderGame();
      });
    });
    this.root.querySelector<HTMLButtonElement>('[data-action="nextChapter"]')?.addEventListener("click", () => {
      this.dispatch({ type: "nextChapter" });
    });
    this.root.querySelectorAll<HTMLButtonElement>('[data-action="reset"]').forEach((button) => {
      button.addEventListener("click", () => this.dispatch({ type: "reset" }));
    });
    this.root.querySelector<HTMLButtonElement>('[data-action="menu"]')?.addEventListener("click", () => {
      this.adapter?.disconnect();
      this.adapter = undefined;
      this.snapshot = undefined;
      this.renderMenu();
    });
    this.root.querySelectorAll<HTMLButtonElement>("[data-chapter]").forEach((button) => {
      button.addEventListener("click", () => {
        this.dispatch({ type: "setChapter", chapterIndex: Number(button.dataset.chapter) });
      });
    });
    this.root.querySelector<HTMLButtonElement>("[data-copy-code]")?.addEventListener("click", async (event) => {
      const value = (event.currentTarget as HTMLButtonElement).textContent ?? "";
      await navigator.clipboard.writeText(value);
    });
  }

  private dispatch(action: GameAction) {
    this.adapter?.dispatch(action);
  }
}

function escapeHtml(value: string) {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}
